import { roRO } from "@mui/x-date-pickers/locales";
import { UserFormSchema } from "../../../common/components/user-inv-modal/api/schema";
import { ReportTransactionType } from "../../../common/enums/report-type";
import {
  FacilityModel,
  FCProductsModel,
  FacilityStatusFilter,
  PagedFacilityModel,
  RoleCheckerResponseModel,
  TransactionProductModel,
} from "./types";
import { create } from "zustand";

export interface IFacilityState {
  initialFacilityState: PagedFacilityModel;

  isChangingAdmin: boolean;
  setChangingAdmin: (isChangingAdmin: boolean) => void;

  isEditModalOpen: boolean;
  setEditModalOpen: (isEditModalOpen: boolean) => void;

  isAddModalOpen: boolean;
  setAddModal: (isAddModalOpen: boolean) => void;

  anchorEl: null | HTMLElement;
  setAnchorEl: (anchorEl: null | HTMLElement) => void;

  activeRowId: undefined | string;
  setActiveRowId: (activeRowId: undefined | string) => void;

  selectedProducts: FCProductsModel[];
  setSselectedProducts: (
    selectedProducts: FCProductsModel[] | undefined,
  ) => void;

  productsAnchorEl: null | HTMLElement;
  setProductsAnchorEl: (productsAnchorEl: null | HTMLElement) => void;

  isDisableFacilityModalOpen: boolean;
  setDisableFacilityModal: (isDisableFacilityModalOpen: boolean) => void;

  isArchiveFacilityModalOpen: boolean;
  setArchiveFacilityModal: (isArchiveFacilityModalOpen: boolean) => void;

  facilityToDisable: FacilityModel | undefined;
  setFacilityToDisable: (facilityToDisable: FacilityModel | undefined) => void;

  filterAnchorEl: null | HTMLElement;
  setFilterAnchorEl: (filterAnchorEl: null | HTMLElement) => void;

  currentStep: number;
  setCurrentStep: (currentStep: number) => void;

  selectedUser?: UserFormSchema;
  setSelectedUser: (user: UserFormSchema | undefined) => void;

  selectedFacility?: FacilityModel;
  setSelectedFacility: (selectedFacility: FacilityModel | undefined) => void;

  isInitializingFacility: boolean;
  setIsInitializingFacility: (val: boolean) => void;

  facilityFormNeedsReload: boolean;
  setFacilityFormNeedsReload: (val: boolean) => void;

  isChangeAdminOpen: boolean;
  setIsChangeAdminOpen: (iChangeAdminOpen: boolean) => void;

  setFacilityPage: (pageNumber: number) => void;
  setSearchText: (searchText: string | undefined) => void;
  setFilters: (
    filters: Partial<{
      isShowAll: boolean;
      isEnabled: boolean;
      isDisabled: boolean;
      statusFilter: FacilityStatusFilter;
    }>,
  ) => void;

  modalTitle: () => string;

  isViewOnly: boolean;
  setIsViewOnly: (isViewOnly: boolean) => void;

  isProductsModalOpen: boolean;
  setProductsModalOpen: (isProductsModalOpen: boolean) => void;

  syncingFacilityId: string | null;
  setSyncingFacilityId: (id: string | null) => void;

  syncingFacilityName: string | null;
  setSyncingFacilityName: (name: string | null) => void;

  isSyncAllRunning: boolean;
  setIsSyncAllRunning: (val: boolean) => void;

  productsModalMode: "products" | "history";
  setProductsModalMode: (mode: "products" | "history") => void;

  selectedTransactionProduct: TransactionProductModel | undefined;
  setSelectedTransactionProduct: (
    val: TransactionProductModel | undefined,
  ) => void;

  exportingId: string | undefined;
  setExportingId: (id: string | undefined) => void;

  reset: () => void;

  transactionFilterValue: number | undefined;
  setTransactionFilterValue: (value: number | undefined) => void;

  openGenerateReportModal: boolean;
  setOpenGenerateReportModal: (open: boolean) => void;

  selectedReportTransaction: ReportTransactionType;
  setSelectedReportTransaction: (value: ReportTransactionType) => void;

  roleConfirmationSubject: RoleCheckerResponseModel | undefined;
  userFormData: UserFormSchema | undefined;
  showRoleConfirmationModal: boolean;
  setShowRoleConfirmationModal: (
    val: boolean,
    subject: RoleCheckerResponseModel | undefined,
    userFormData?: UserFormSchema,
  ) => void;

  activeUser: undefined | FacilityModel;
  setActiveUser: (val: FacilityModel | undefined) => void;

  isResetPassModalOpen: boolean;
  setResetPassModal: (val: boolean) => void;

  isConfirmPassModalOpen: boolean;
  setConfirmPassModal: (val: boolean) => void;

  isUserCredentialsSent: boolean;
  setUserCredentialsSentModal: (val: boolean) => void;

  resetSearch: () => void;
}

// ---- Initial State ----
const initialState = {
  initialFacilityState: {
    items: [],
    totalItems: 0,
    pageNumber: 1,
    pageSize: 10,
    searchText: undefined,
    currentStep: 0,
    statusFilter: "all" as FacilityStatusFilter,
    isShowAll: true,
    isEnabled: true,
    isDisabled: true,
  },
  isViewOnly: false,
  isChangingAdmin: false,
  isAddModalOpen: false,
  isEditModalOpen: false,
  anchorEl: null as null | HTMLElement,
  activeRowId: undefined as undefined | string,
  selectedProducts: [] as FCProductsModel[],
  productsAnchorEl: null as null | HTMLElement,
  isDisableFacilityModalOpen: false,
  isArchiveFacilityModalOpen: false,
  facilityToDisable: undefined as FacilityModel | undefined,
  filterAnchorEl: null as null | HTMLElement,
  currentStep: 0,
  selectedUser: undefined as UserFormSchema | undefined,
  selectedFacility: undefined as FacilityModel | undefined,
  isInitializingFacility: false,
  facilityFormNeedsReload: false,
  isChangeAdminOpen: false,
  syncingFacilityId: null,
  syncingFacilityName: null,
  isSyncAllRunning: false,
  transactionFilterValue: 0,
  productsModalMode: "products" as "products" | "history",
  selectedTransactionProduct: undefined as TransactionProductModel | undefined,
  exportingId: undefined as string | undefined,
  openGenerateReportModal: false,
  roleConfirmationSubject: undefined,
  showRoleConfirmationModal: false,
  userFormData: undefined,
  selectedReportTransaction: ReportTransactionType.AllTransactions, // initial value
  activeUser: undefined,
  isResetPassModalOpen: false,
  isConfirmPassModalOpen: false,
  isUserCredentialsSent: false,
};

// ---- Store ----
const useFacilityStore = create<IFacilityState>((set, get) => ({
  ...initialState,
  setShowRoleConfirmationModal: (
    showRoleConfirmationModal: boolean,
    subject: RoleCheckerResponseModel | undefined,
    userFormData?: UserFormSchema,
  ) =>
    set({
      showRoleConfirmationModal: showRoleConfirmationModal,
      roleConfirmationSubject: subject,
      userFormData: userFormData,
    }),
  setResetPassModal: (isResetPassModalOpen) => set({ isResetPassModalOpen }),
  setConfirmPassModal: (isConfirmPassModalOpen) =>
    set({ isConfirmPassModalOpen }),
  setActiveUser: (activeUser) => set({ activeUser }),
  setUserCredentialsSentModal: (isUserCredentialsSent) =>
    set({ isUserCredentialsSent }),
  setIsViewOnly: (isViewOnly) => set({ isViewOnly }),
  setChangingAdmin: (isChangingAdmin) => set({ isChangingAdmin }),
  setAddModal: (isAddModalOpen) => set({ isAddModalOpen }),
  setEditModalOpen: (isEditModalOpen) => set({ isEditModalOpen }),
  setAnchorEl: (anchorEl) => set({ anchorEl }),
  setActiveRowId: (activeRowId) => set({ activeRowId }),
  setSselectedProducts: (selectedProducts) =>
    set({ selectedProducts: selectedProducts ?? [] }),
  setProductsAnchorEl: (productsAnchorEl) => set({ productsAnchorEl }),
  setDisableFacilityModal: (isDisableFacilityModalOpen) =>
    set({ isDisableFacilityModalOpen }),
  setArchiveFacilityModal: (isArchiveFacilityModalOpen) =>
    set({ isArchiveFacilityModalOpen }),
  setFacilityToDisable: (facilityToDisable) => set({ facilityToDisable }),
  setFilterAnchorEl: (filterAnchorEl) => set({ filterAnchorEl }),
  setCurrentStep: (currentStep) => set({ currentStep }),
  setSelectedUser: (user) => set({ selectedUser: user }),
  setSelectedFacility: (selectedFacility) => set({ selectedFacility }),
  setIsInitializingFacility: (val) => set({ isInitializingFacility: val }),
  setFacilityFormNeedsReload: (val) => set({ facilityFormNeedsReload: val }),
  setIsChangeAdminOpen: (isChangeAdminOpen) => set({ isChangeAdminOpen }),
  setSelectedReportTransaction: (value) =>
    set(() => ({ selectedReportTransaction: value })),
  setFacilityPage: (pageNumber: number) =>
    set((state) => ({
      initialFacilityState: { ...state.initialFacilityState, pageNumber },
    })),
  setSearchText: (searchText: string | undefined) =>
    set((state) => ({
      initialFacilityState: { ...state.initialFacilityState, searchText },
    })),
  setFilters: (filters) =>
    set((state) => ({
      initialFacilityState: {
        ...state.initialFacilityState,
        ...filters,
        pageNumber: 1,
      },
    })),

  setTransactionFilterValue: (value) => set({ transactionFilterValue: value }),

  setSyncingFacilityId: (id) => set({ syncingFacilityId: id }),

  setSyncingFacilityName: (name) => set({ syncingFacilityName: name }),

  setIsSyncAllRunning: (val) => set({ isSyncAllRunning: val }),

  setOpenGenerateReportModal: (openGenerateReportModal) =>
    set({ openGenerateReportModal }),

  modalTitle: () => {
    const {
      currentStep,
      isEditModalOpen,
      isChangeAdminOpen,
      isChangingAdmin,
      isViewOnly,
    } = get();
    switch (currentStep) {
      case 0:
        if (isEditModalOpen) {
          if (isChangeAdminOpen && !isChangingAdmin) {
            return isViewOnly ? "Facility Admin" : "Change Facility Admin";
          } else {
            return isViewOnly ? "Facility Admin" : "Edit Facility Admin";
          }
        } else {
          return "Add Facility Admin";
        }
      case 1:
        if (isEditModalOpen) {
          return isViewOnly ? "Products" : "Edit Products";
        } else {
          return "Assign Products";
        }

      case 2:
        return isViewOnly ? "Facility Details" : "Set-up Facility";
      default:
        return "";
    }
  },

  isProductsModalOpen: false,
  setProductsModalOpen: (isProductsModalOpen) => set({ isProductsModalOpen }),
  setProductsModalMode: (productsModalMode) => set({ productsModalMode }),

  setSelectedTransactionProduct(selectedTransactionProduct) {
    set({ selectedTransactionProduct });
  },
  setExportingId: (exportingId) => set({ exportingId }),

  reset: () => set(initialState),

  resetSearch: () =>
    set((state) => ({
      initialFacilityState: {
        ...state.initialFacilityState,
        searchText: undefined,
        pageNumber: 1,
      },
    })),
}));

export default useFacilityStore;
